/*
 * Scentt product page components (deferred, needs scentt-core.js):
 *   <scentt-delivery>        delivery estimate with UK cut-off
 *   <scentt-dialog-trigger>  opens a <scentt-dialog> (concentration guide)
 *   <scentt-dialog>          wraps a native <dialog>: close on backdrop / Escape, "more" hint, focus return
 *   <scentt-swap>            re-renders itself from Trade's variant-change HTML (low stock, instalments, notify me\u2026)
 *   <scentt-sticky-atc>      sticky add-to-bag bar
 *   <scentt-atc>             add to bag with human-readable errors (wraps Trade <product-form>)
 *   <scentt-notify>          "notify me" email check
 *   <scentt-review-form>     "Write a review" toggle
 */
(() => {
  if (!window.Scentt) return;
  const S = window.Scentt && Object.isFrozen(window.Scentt) && typeof window.Scentt.html === 'function' ? window.Scentt : null;
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const ID_RE = /^[A-Za-z0-9_-]{1,120}$/;

  /* Trade's pub/sub helpers (pubsub.js / constants.js) \u2013 optional */
  const variantEvent = () => (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) || 'variant-change';
  const onVariantChange = (fn) => {
    if (typeof subscribe !== 'function') return () => {};
    try { return subscribe(variantEvent(), fn) || (() => {}); } catch (e) { S.warn(e); return () => {}; }
  };

  /* =====================================================================
     Delivery estimate
     ===================================================================== */
  const EN = {
    days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    ordinals: ['th', 'st', 'nd', 'rd'],
    units: ['hr', 'hrs', 'min', 'mins'],
  };
  const csv = (value, size, def) => {
    const arr = String(value || '').split(',').map((s) => s.trim().slice(0, 40));
    return arr.length === size && arr.every((s) => s && !/[<>{}\[\]]/.test(s)) ? arr : def;
  };
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  /** Wall-clock parts of `date` in time zone `tz` (falls back to the browser's zone). */
  const zoned = (date, tz) => {
    try {
      const parts = {};
      new Intl.DateTimeFormat('en-GB', {
        timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23', weekday: 'short',
      }).formatToParts(date).forEach((p) => { parts[p.type] = p.value; });
      return { y: +parts.year, m: +parts.month, d: +parts.day, h: +parts.hour % 24, min: +parts.minute };
    } catch (e) {
      return { y: date.getFullYear(), m: date.getMonth() + 1, d: date.getDate(), h: date.getHours(), min: date.getMinutes() };
    }
  };
  const isoDay = (d) => d.toISOString().slice(0, 10);

  /**
   * Pure calculation (exposed for tests): returns { sameDay, minutesLeft, from, to } where from/to are UTC-midnight Dates.
   * opts: { cutoff (hour 0-24), min, max (working days), saturday (bool), closed (['YYYY-MM-DD']), tz }
   */
  const estimate = (now, opts) => {
    const cutoff = S.int(opts.cutoff, 14, 0, 24);
    const min = S.int(opts.min, 1, 0, 30);
    const max = Math.max(min, S.int(opts.max, min, 0, 30));
    const closed = new Set((opts.closed || []).filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s)));
    const t = zoned(now, opts.tz || 'Europe/London');
    const dispatchOk = (d) => { const w = d.getUTCDay(); return w !== 0 && w !== 6 && !closed.has(isoDay(d)); };
    const deliveryOk = (d) => { const w = d.getUTCDay(); return w !== 0 && (opts.saturday || w !== 6) && !closed.has(isoDay(d)); };
    const today = new Date(Date.UTC(t.y, t.m - 1, t.d));
    const nowMinutes = t.h * 60 + t.min;
    const sameDay = dispatchOk(today) && nowMinutes < cutoff * 60;
    const dispatch = new Date(today);
    let guard = 0;
    if (!sameDay) {
      do { dispatch.setUTCDate(dispatch.getUTCDate() + 1); guard += 1; } while (!dispatchOk(dispatch) && guard < 400);
    }
    const add = (n) => {
      const d = new Date(dispatch);
      let count = 0;
      let steps = 0;
      while (count < n && steps < 400) { d.setUTCDate(d.getUTCDate() + 1); steps += 1; if (deliveryOk(d)) count += 1; }
      return d;
    };
    return { sameDay, minutesLeft: sameDay ? cutoff * 60 - nowMinutes : 0, from: add(min), to: add(max) };
  };

  const ordinal = (n, ords) => {
    const t = n % 100;
    if (t >= 11 && t <= 13) return ords[0];
    return ords[n % 10] || ords[0];
  };

  class ScenttDelivery extends HTMLElement {
    static estimate(now, opts) { return estimate(now, opts); }

    connectedCallback() {
      const d = this.dataset;
      this.opts = {
        cutoff: S.int(d.cutoff, 14, 0, 24),
        min: S.int(d.min, 1, 0, 30),
        max: S.int(d.max, 2, 0, 30),
        saturday: d.saturday === 'true',
        closed: String(d.closed || '').split(',').map((s) => s.trim()).filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s)).slice(0, 100),
        tz: /^[A-Za-z_]+(\/[A-Za-z_+-]+){0,2}$/.test(d.tz || '') ? d.tz : 'Europe/London',
      };
      this.words = {
        days: csv(d.days, 7, EN.days),
        months: csv(d.months, 12, EN.months),
        ordinals: csv(d.ordinals, 4, EN.ordinals),
        units: csv(d.units, 4, EN.units),
      };
      this.tpl = String(d.text || '').slice(0, 300) || 'Order within [time] for delivery [date]';
      this.tplAfter = String(d.textAfter || '').slice(0, 300) || 'Order now for delivery [date]';
      this.out = this.querySelector('[data-delivery-text]');
      this.signature = '';
      this.render();
      clearInterval(this.timer);
      this.timer = setInterval(() => this.render(), 30000);
    }

    disconnectedCallback() { clearInterval(this.timer); }

    timeText(minutes) {
      const [hr, hrs, mn, mns] = this.words.units;
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return [h ? `${h} ${h === 1 ? hr : hrs}` : '', m || !h ? `${m} ${m === 1 ? mn : mns}` : ''].filter(Boolean).join(' ');
    }

    /** Appends "Thursday 8<sup>th</sup> October" to `parent`. */
    appendDate(parent, date) {
      const n = date.getUTCDate();
      parent.append(`${this.words.days[date.getUTCDay()]} ${n}`);
      const sup = document.createElement('sup');
      sup.textContent = ordinal(n, this.words.ordinals);
      parent.append(sup, ` ${this.words.months[date.getUTCMonth()]}`);
    }

    render(now = new Date()) {
      if (!this.out) return;
      const r = estimate(now, this.opts);
      const showRange = r.to.getTime() !== r.from.getTime();
      const signature = [r.sameDay, r.minutesLeft, isoDay(r.from), isoDay(r.to)].join('|');
      if (signature !== this.signature) {
        this.signature = signature;
        const tpl = r.sameDay ? this.tpl : this.tplAfter;
        const frag = document.createDocumentFragment();
        tpl.split(/(\[time\]|\[date\])/).forEach((piece) => {
          if (piece === '[time]') {
            const strong = document.createElement('strong');
            strong.textContent = this.timeText(r.minutesLeft);
            frag.append(strong);
          } else if (piece === '[date]') {
            const strong = document.createElement('strong');
            this.appendDate(strong, r.from);
            if (showRange) { strong.append(' \u2013 '); this.appendDate(strong, r.to); }
            frag.append(strong);
          } else if (piece) {
            frag.append(document.createTextNode(piece));
          }
        });
        this.out.replaceChildren(frag);
      }
      this.hidden = false;
    }
  }
  define('scentt-delivery', ScenttDelivery);

  /* =====================================================================
     Dialogs (concentration guide)
     ===================================================================== */
  class ScenttDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || this.bound) return;
      this.bound = true;
      this.more = this.dialog.querySelector('[data-scentt-dialog-more]');
      this.onScroll = () => this.updateMore();
      this.onResize = S.debounce(() => this.updateMore(), 100);
      this.dialog.addEventListener('scroll', this.onScroll, { passive: true });
      this.dialog.addEventListener('click', (e) => {
        if (e.target.closest('[data-scentt-dialog-close]')) { this.close(); return; }
        if (e.target.closest('[data-scentt-dialog-more]')) {
          this.dialog.scrollBy({ top: this.dialog.clientHeight * 0.7, behavior: S.reducedMotion ? 'auto' : 'smooth' });
          return;
        }
        if (e.target === this.dialog) {
          const r = this.dialog.getBoundingClientRect();
          const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
          if (!inside) this.close();
        }
      });
      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('overflow-hidden');
        window.removeEventListener('resize', this.onResize);
        const back = this.opener;
        this.opener = null;
        if (back && back.isConnected) back.focus();
      });
    }

    disconnectedCallback() { window.removeEventListener('resize', this.onResize); }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener || document.activeElement;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal(); else this.dialog.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      this.dialog.scrollTop = 0;
      window.addEventListener('resize', this.onResize, { passive: true });
      requestAnimationFrame(() => this.updateMore());
    }

    close() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function') this.dialog.close(); else this.dialog.removeAttribute('open');
    }

    updateMore() {
      if (!this.more || !this.dialog) return;
      const d = this.dialog;
      const atEnd = d.scrollTop + d.clientHeight >= d.scrollHeight - 12;
      this.more.classList.toggle('is-hidden', atEnd);
    }
  }
  define('scentt-dialog', ScenttDialog);

  class ScenttDialogTrigger extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button, a');
        if (!btn) return;
        const id = this.dataset.dialog || '';
        if (!ID_RE.test(id)) return;
        const dlg = document.getElementById(id);
        const host = dlg && dlg.closest('scentt-dialog');
        if (!host || typeof host.open !== 'function') return;
        e.preventDefault();
        host.open(btn);
      });
    }
  }
  define('scentt-dialog-trigger', ScenttDialogTrigger);

  /* =====================================================================
     Variant-aware blocks: re-render from Trade's variant-change HTML
     (that HTML comes from our own theme via the Section Rendering API)
     ===================================================================== */
  class ScenttSwap extends HTMLElement {
    connectedCallback() {
      if (this.off) return;
      this.off = onVariantChange((event) => this.update(event && event.data));
    }

    disconnectedCallback() { if (this.off) { this.off(); this.off = null; } }

    update(data) {
      if (!data || !data.html || typeof data.html.getElementById !== 'function' || !ID_RE.test(this.id)) return;
      const info = this.closest('product-info');
      const own = info ? info.dataset.section || '' : '';
      const expected = info ? info.sectionId || own : '';
      if (!own || data.sectionId !== expected) return;
      const fresh = data.html.getElementById(this.id.replace(own, data.sectionId));
      if (!fresh) return;
      this.replaceChildren(...Array.from(fresh.childNodes).map((n) => document.importNode(n, true)));
      this.hidden = fresh.hasAttribute('hidden');
    }
  }
  define('scentt-swap', ScenttSwap);

  /* =====================================================================
     Sticky add to bag
     ===================================================================== */
  /* Body classes other components use to lift floating buttons above the bar
     (scentt-compare.css uses the first, scentt-global.css the second). */
  const BODY_CLASSES = ['scentt-sticky-on'];

  class ScenttStickyAtc extends HTMLElement {
    connectedCallback() {
      const sid = this.dataset.section || '';
      if (!ID_RE.test(sid) || this.ready || this.closest('quick-add-modal, .quick-add-modal')) return;
      this.ready = true;
      this.info = document.getElementById(`MainProduct-${sid}`);
      this.sid = sid;
      this.btn = this.querySelector('[data-sticky-add]');
      this.priceBox = this.querySelector('[data-sticky-price]');
      this.variantBox = this.querySelector('[data-sticky-variant]');
      this.setVisible(false);
      if (!this.info || !this.btn) return;
      this.btn.addEventListener('click', () => {
        this.sync();
        if (this.mode === 'notify') { this.goToNotify(); return; }
        const sub = this.submit();
        if (this.mode === 'add' && sub && !sub.disabled) sub.click();
      });
      this.off = onVariantChange(() => setTimeout(() => this.sync(), 60));
      const target = this.info.querySelector('.product-form__buttons') || this.info.querySelector('product-form');
      if (!target || !('IntersectionObserver' in window)) return;
      this.above = true;
      this.footerSeen = false;
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.target === target) this.above = en.isIntersecting || en.boundingClientRect.top > 0;
          else this.footerSeen = en.isIntersecting;
        });
        this.sync();
        this.setVisible(!this.above && !this.footerSeen);
      });
      this.io.observe(target);
      const footer = document.querySelector('.scentt-footer, footer.footer, footer');
      if (footer) this.io.observe(footer);
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.off) this.off();
      this.ready = false;
      document.body.classList.remove(...BODY_CLASSES);
    }

    submit() { return this.info && this.info.querySelector(`#ProductSubmitButton-${this.sid}`); }

    setVisible(on) {
      this.classList.toggle('is-visible', on);
      this.toggleAttribute('inert', !on);
      this.setAttribute('aria-hidden', on ? 'false' : 'true');
      BODY_CLASSES.forEach((c) => document.body.classList.toggle(c, on));
    }

    sync() {
      const sub = this.submit();
      const price = this.info.querySelector(`#price-${this.sid} .price`);
      if (price && this.priceBox) {
        const node = price.classList.contains('price--on-sale')
          ? price.querySelector('.price__sale .price-item--sale')
          : price.querySelector('.price__regular .price-item--regular');
        const text = ((node || price).textContent || '').replace(/\s+/g, ' ').trim();
        if (text) this.priceBox.textContent = text.slice(0, 60);
      }
      if (this.variantBox) {
        const picked = [];
        this.info.querySelectorAll('variant-selects input:checked, variant-selects select').forEach((x) => {
          const v = x.tagName === 'SELECT' ? x.options[x.selectedIndex] && x.options[x.selectedIndex].text : x.value;
          if (v && !/default title/i.test(v)) picked.push(String(v).trim().slice(0, 40));
        });
        this.variantBox.textContent = picked.join(' \u00b7 ');
      }
      // What the bar's button can do for the chosen size: add it, offer the back-in-stock alert, or nothing.
      const coming = this.dataset.coming === 'true';
      const unavailable = coming || !sub || sub.disabled || sub.getAttribute('aria-disabled') === 'true';
      const label = (key, fallback) => String(this.dataset[key] || fallback || '').slice(0, 60);
      if (!unavailable) {
        this.setMode('add', ((sub.querySelector('span') || sub).textContent || '').trim().slice(0, 60));
      } else if (this.notifyForm()) {
        this.setMode('notify', label('labelNotify', 'Notify me'));
      } else {
        this.setMode('off', coming ? label('labelComing', 'Coming soon') : label('labelSoldout', 'Sold out'));
      }
    }

    /** The visible back-in-stock / coming-soon form on this product, if any */
    notifyForm() {
      const wrap = this.info && this.info.querySelector('.scentt-notify-wrap:not([hidden]) scentt-notify, .scentt-notify-wrap:not([hidden]) .scentt-notify');
      return wrap && wrap.getClientRects().length ? wrap : null;
    }

    setMode(mode, text) {
      this.mode = mode;
      this.dataset.mode = mode;
      if (text) this.btn.textContent = text;
      this.btn.disabled = mode === 'off';
      this.btn.classList.toggle('button--primary', mode === 'add');
      this.btn.classList.toggle('button--secondary', mode === 'notify');
      this.btn.classList.toggle('scentt-coming-btn', mode === 'off');
    }

    goToNotify() {
      const form = this.notifyForm();
      if (!form) return;
      form.scrollIntoView({ behavior: S && S.reducedMotion ? 'auto' : 'smooth', block: 'center' });
      const input = form.querySelector('input[type="email"], input:not([type="hidden"])');
      if (input) setTimeout(() => input.focus({ preventScroll: true }), S && S.reducedMotion ? 0 : 450);
    }
  }
  define('scentt-sticky-atc', ScenttStickyAtc);

  /* =====================================================================
     Add to bag with human-readable errors.
     <scentt-atc> wraps Trade's <product-form>. It takes the submit over in the capture phase (so Trade's
     own handler does not run) and posts through Scentt.fetchJSON: 10 s timeout, same origin, and on a
     422 / server / network failure an inline message + toast built with textContent only.
     Gift card recipient forms (data-hide-errors="true") stay with Trade, which maps field errors.
     ===================================================================== */
  const plainText = (value, max = 200) => {
    let text = '';
    try { text = new DOMParser().parseFromString(String(value || ''), 'text/html').body.textContent || ''; } catch (e) { text = ''; }
    return text.replace(/\s+/g, ' ').trim().slice(0, max);
  };
  const pub = (name, data) => {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS[name]) return;
    try { publish(PUB_SUB_EVENTS[name], data); } catch (e) { S.warn(e); }
  };

  class ScenttAtc extends HTMLElement {
    connectedCallback() {
      if (this.onSubmit) return;
      this.onSubmit = (e) => this.submit(e);
      this.addEventListener('submit', this.onSubmit, true);
    }

    disconnectedCallback() {
      if (this.onSubmit) this.removeEventListener('submit', this.onSubmit, true);
      this.onSubmit = null;
    }

    message(err) {
      const fallback = plainText(this.dataset.error) || S.config.strings.error;
      if (!navigator.onLine) return plainText(this.dataset.offline) || S.config.strings.offline;
      const own = plainText(err && err.userMessage);
      return own && own !== S.config.strings.error ? own : fallback;
    }

    showError(pf, text) {
      const wrap = pf.querySelector('.product-form__error-message-wrapper');
      const box = wrap && wrap.querySelector('.product-form__error-message');
      if (!wrap || !box) return;
      box.textContent = text;
      wrap.hidden = !text;
    }

    setBusy(pf, btn, on) {
      this.busy = on;
      btn.classList.toggle('loading', on);
      if (on) btn.setAttribute('aria-disabled', 'true'); else btn.removeAttribute('aria-disabled');
      const spinner = pf.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !on);
    }

    async submit(e) {
      const form = e.target;
      const pf = form && form.closest ? form.closest('product-form') : null;
      if (!pf || !this.contains(pf) || form.getAttribute('data-type') !== 'add-to-cart-form' || pf.dataset.hideErrors === 'true') return;
      e.preventDefault();
      e.stopPropagation();
      const btn = form.querySelector('button[name="add"]') || form.querySelector('[type="submit"]');
      if (!btn || this.busy || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

      this.showError(pf, '');
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = new FormData(form);
      if (cart && typeof cart.getSectionsToRender === 'function') {
        try {
          body.append('sections', cart.getSectionsToRender().map((s) => s.id));
          body.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        } catch (err) { S.warn(err); }
      }
      const variantId = String(body.get('id') || '');
      this.setBusy(pf, btn, true);
      try {
        const data = await S.fetchJSON('/cart/add.js', { method: 'POST', body, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!cart) { window.location.href = S.config.cartUrl; return; }
        pub('cartUpdate', { source: 'product-form', productVariantId: variantId, cartData: data });
        S.emit('product:added', { variantId });
        const render = () => {
          cart.classList.remove('is-empty');
          try { cart.renderContents(data); } catch (err) { S.warn(err); }
        };
        const modal = this.closest('quick-add-modal');
        if (modal && typeof modal.hide === 'function') {
          document.body.addEventListener('modalClosed', () => setTimeout(render), { once: true });
          modal.hide(true);
        } else {
          render();
        }
      } catch (err) {
        const text = this.message(err);
        this.showError(pf, text);
        S.toast(text, 'error');
        pub('cartError', { source: 'product-form', productVariantId: variantId, errors: text, message: text });
      } finally {
        this.setBusy(pf, btn, false);
      }
    }
  }
  define('scentt-atc', ScenttAtc);

  /* =====================================================================
     "Notify me" form: checks the email before the page is posted, shows a plain-text message
     (server errors from Shopify are revealed by scentt-core.js on the submitted form only)
     ===================================================================== */
  const EMAIL_RE = /^[^\s@<>"'`]{1,64}@[^\s@<>"'`.]+(\.[^\s@<>"'`.]+)+$/;
  class ScenttNotify extends HTMLElement {
    connectedCallback() {
      if (this.onSubmit) return;
      this.onSubmit = (e) => this.check(e);
      this.onInput = (e) => { if (e.target.matches && e.target.matches('input[type="email"]')) this.say(''); };
      this.addEventListener('submit', this.onSubmit, true);
      this.addEventListener('input', this.onInput);
    }

    disconnectedCallback() {
      this.removeEventListener('submit', this.onSubmit, true);
      this.removeEventListener('input', this.onInput);
      this.onSubmit = null;
    }

    say(text) {
      const box = this.querySelector('[data-notify-message]');
      const input = this.querySelector('input[type="email"]');
      if (input) { if (text) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid'); }
      if (!box) return;
      box.textContent = text;
      box.hidden = !text;
    }

    check(e) {
      const form = e.target;
      const input = form && form.querySelector ? form.querySelector('input[type="email"]') : null;
      if (!input) return;
      const value = String(input.value || '').trim();
      let problem = '';
      if (!navigator.onLine) problem = plainText(this.dataset.offline) || S.config.strings.offline;
      else if (value.length > 200 || !EMAIL_RE.test(value)) problem = plainText(this.dataset.invalid) || S.config.strings.error;
      if (problem) {
        e.preventDefault();
        e.stopPropagation();
        this.say(problem);
        input.focus();
        return;
      }
      input.value = value;
      if (this.sending) { e.preventDefault(); return; }
      this.sending = true;
      const btn = form.querySelector('[type="submit"]');
      if (btn) btn.setAttribute('aria-busy', 'true');
    }
  }
  define('scentt-notify', ScenttNotify);

  /* =====================================================================
     Product reviews: "Write a review" toggle
     ===================================================================== */
  class ScenttReviewForm extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('[data-scentt-review-toggle]');
      const id = this.btn ? this.btn.getAttribute('aria-controls') || '' : '';
      this.form = ID_RE.test(id) ? document.getElementById(id) : null;
      if (!this.btn || !this.form || this.bound) return;
      this.bound = true;
      this.btn.addEventListener('click', () => {
        const open = !this.form.classList.contains('is-open');
        this.set(open);
        if (open) { const first = this.form.querySelector('input[type="radio"], input:not([type="hidden"])'); if (first) first.focus(); }
      });
      const init = () => this.set(Boolean(this.form.querySelector('[data-scentt-success]:not([hidden]), [data-scentt-error]:not([hidden])')));
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
    }

    set(open) {
      this.form.classList.toggle('is-open', open);
      this.btn.setAttribute('aria-expanded', String(open));
    }
  }
  define('scentt-review-form', ScenttReviewForm);
})();
